/**
 * One **sound preference for every video** on the site (article feed, Reels and
 * stories): turning the sound on in one place turns it on everywhere.
 *
 * Stored in localStorage (`ms_reel_sound`, ON by default) and broadcast with a
 * window event, so every player stays in sync.
 *
 * The “only **one media plays at a time**” rule (videos **and** mp3) lives in
 * `utils/mediaPlayback.js`; `claimPlayback` / `releasePlayback` below are thin
 * wrappers over it so every existing player keeps working — and an embed
 * (`<iframe>`) can now take the slot too by handing over its own stop callback.
 */
import { useEffect, useState } from 'react';
import { releasePlayback as releaseMedia, requestPlayback } from './mediaPlayback';

export const SOUND_KEY = 'ms_reel_sound';
const EVENT = 'ms-video-sound';

/** true → videos may play with sound (used as the autoplay default too). */
export function isVideoSoundOn() {
  try {
    return localStorage.getItem(SOUND_KEY) !== '0';   // default: on
  } catch (e) {
    return true;                                      // private mode
  }
}

/** Remember the choice + tell every player about it. */
export function setVideoSound(on) {
  try {
    localStorage.setItem(SOUND_KEY, on ? '1' : '0');
  } catch (e) { /* private mode — in-memory only */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { on: !!on } }));
  }
}

/** React state that follows the shared preference. */
export function useVideoSound() {
  const [on, setOn] = useState(isVideoSoundOn);
  useEffect(() => {
    const sync = (e) => setOn(e && e.detail ? !!e.detail.on : isVideoSoundOn());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);        // changed in another tab
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return [on, setVideoSound];
}

/**
 * Remember the clip that is currently allowed to play, and stop the old one
 * (whatever it is: another clip, an embed or an mp3 — see `mediaPlayback.js`).
 */
let current = null;

export function claimPlayback(el) {
  current = el;
  requestPlayback(el, () => { try { el.pause(); } catch (e) { /* ignore */ } });
}

export function releasePlayback(el) {
  if (current === el) current = null;
  releaseMedia(el);
}

/**
 * Start a clip. `withSound` attempts an **unmuted** start; when the browser
 * blocks it (no gesture yet) it silently falls back to muted playback.
 *
 * @returns {'sound'|'muted'|'blocked'|'failed'} — `blocked` = the viewer wants
 *          sound but the browser refused, so show the “tap to turn the sound on”.
 */
export async function playClip(el, withSound) {
  if (!el) return 'failed';
  try {
    el.muted = !withSound;
    await el.play();
    return withSound ? 'sound' : 'muted';
  } catch (e) {
    if (!withSound) return 'failed';
    el.muted = true;
    try {
      await el.play();
      return 'blocked';
    } catch (e2) {
      return 'failed';
    }
  }
}
